import { useEffect, useState, useRef } from 'react'
import { employeeService, EmployeeFilters, CreateEmployeeData, UpdateEmployeeData } from '../services/employeeService'
import { authService } from '../services/authService'
import { Employee, WORK_CENTER_LABELS, SHIFT_LABELS } from '../types'
import { Search, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, MessageSquare, UserCheck, UserX, Users, Eye, LayoutGrid, List, Briefcase, MapPin, Clock, X, LogIn, Power } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../contexts/AuthContext'
import { EmployeeFormModal } from '../components/employees/EmployeeFormModal'
import { EmployeeNotesModal } from '../components/employees/EmployeeNotesModal'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { extractApiErrors } from '../utils/errorHandler'

// Hook para detectar si es móvil
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

// Componente Card para vista móvil
interface EmployeeCardProps {
  employee: Employee
  isAdmin: boolean
  onView: (employee: Employee) => void
  onToggle: (employee: Employee) => void
  onEdit: (employee: Employee) => void
  onNotes: (employee: Employee) => void
  onDelete: (employee: Employee) => void
  onImpersonate: (employee: Employee) => void
}

function EmployeeCard({ employee, isAdmin, onView, onToggle, onEdit, onNotes, onDelete, onImpersonate }: EmployeeCardProps) {
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3 sm:p-4 shadow-sm ${!employee.isActive ? 'opacity-60' : ''}`}>
      {/* Header con avatar y nombre */}
      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-base sm:text-lg flex-shrink-0">
            {employee.fullName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
              {employee.fullName}
            </p>
            <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
              {employee.email || 'Sin email'}
            </p>
          </div>
        </div>
        <span className="font-mono text-[10px] sm:text-xs bg-gray-100 dark:bg-slate-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-gray-700 dark:text-gray-300 flex-shrink-0">
          {employee.employeeId}
        </span>
      </div>

      {/* Info del puesto */}
      <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
          <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300 truncate">{employee.position}</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
          <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300 truncate">{employee.workCenterName || WORK_CENTER_LABELS[employee.workCenterType]}</span>
        </div>
        <div className="flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-700 dark:text-gray-300">{SHIFT_LABELS[employee.shift]}</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
            {employee.daysAt5Shifts.toLocaleString()} días 5R
          </span>
        </div>
      </div>

      {/* Badges de estado */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-2 sm:mb-3">
        {employee.hasUser ? (
          <span className="badge badge-success text-[10px] sm:text-xs px-1.5 sm:px-2.5 py-0.5 sm:py-1">
            <UserCheck className="h-3 w-3 mr-0.5 sm:mr-1" />
            Con acceso
          </span>
        ) : (
          <span className="badge bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300 text-[10px] sm:text-xs px-1.5 sm:px-2.5 py-0.5 sm:py-1">
            <UserX className="h-3 w-3 mr-0.5 sm:mr-1" />
            Sin acceso
          </span>
        )}
        {!employee.isActive && (
          <span className="badge badge-error text-[10px] sm:text-xs px-1.5 sm:px-2.5 py-0.5 sm:py-1">Inactivo</span>
        )}
      </div>

      {/* Botones de acción */}
      {isAdmin && (
        <div className="grid grid-cols-6 gap-1 sm:gap-1.5 pt-2 sm:pt-3 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={() => onView(employee)}
            className="flex flex-col items-center justify-center p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">Ver</span>
          </button>
          <button
            onClick={() => onToggle(employee)}
            className={`flex flex-col items-center justify-center p-1.5 sm:p-2 rounded-lg transition-colors ${
              employee.isActive
                ? 'hover:bg-warning-50 dark:hover:bg-warning-900/30 text-warning-600 dark:text-warning-400'
                : 'hover:bg-success-50 dark:hover:bg-success-900/30 text-success-600 dark:text-success-400'
            }`}
            title={employee.isActive ? 'Desactivar' : 'Activar'}
          >
            <Power className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">{employee.isActive ? 'Desact.' : 'Activar'}</span>
          </button>
          <button
            onClick={() => onImpersonate(employee)}
            className="flex flex-col items-center justify-center p-1.5 sm:p-2 hover:bg-success-50 dark:hover:bg-success-900/30 rounded-lg text-success-600 dark:text-success-400 transition-colors"
            title="Entrar como este empleado"
          >
            <LogIn className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">Entrar</span>
          </button>
          <button
            onClick={() => onNotes(employee)}
            className="flex flex-col items-center justify-center p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
            title="Notas"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">Notas</span>
          </button>
          <button
            onClick={() => onEdit(employee)}
            className="flex flex-col items-center justify-center p-1.5 sm:p-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 transition-colors"
            title="Editar"
          >
            <Edit2 className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">Editar</span>
          </button>
          <button
            onClick={() => onDelete(employee)}
            className="flex flex-col items-center justify-center p-1.5 sm:p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 transition-colors"
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
            <span className="text-[9px] sm:text-[10px] mt-0.5 hidden xs:block">Borrar</span>
          </button>
        </div>
      )}
    </div>
  )
}

export function EmployeesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const isMobile = useIsMobile()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [filters, setFilters] = useState<EmployeeFilters>({ page: 1, limit: 10, isActive: true })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })

  // Modals state
  const [showFormModal, setShowFormModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showToggleConfirm, setShowToggleConfirm] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Estados para autocomplete
  const [searchTerm, setSearchTerm] = useState('')
  const [autocompleteResults, setAutocompleteResults] = useState<Employee[]>([])
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  // En desktop, por defecto tabla; en móvil, por defecto cards
  useEffect(() => {
    setViewMode(isMobile ? 'cards' : 'table')
  }, [])

  // Autocomplete: buscar mientras escribe (debounce)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setAutocompleteResults([])
      return
    }

    setIsSearching(true)
    const timeoutId = setTimeout(async () => {
      try {
        const response = await employeeService.getAll({
          limit: 8,
          search: searchTerm.trim(),
        })
        setAutocompleteResults(response.data)
      } catch (error) {
        console.error('Error en autocomplete:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  // Cerrar autocomplete al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setIsAutocompleteOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounce para búsqueda principal (filtra la tabla)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm.trim() || undefined, page: 1 }))
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  const fetchEmployees = async () => {
    setIsLoading(true)
    try {
      const response = await employeeService.getAll(filters)
      setEmployees(response.data)
      setPagination(response.pagination)
    } catch (error) {
      console.error('Error fetching employees:', error)
      toast.error('Error al cargar empleados')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchEmployees()
  }, [filters])

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }))
  }

  // Seleccionar empleado del autocomplete - navega al detalle
  const handleSelectFromAutocomplete = (employee: Employee) => {
    setIsAutocompleteOpen(false)
    navigate(`/employees/${employee.id}`)
  }

  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchTerm('')
    setFilters((prev) => ({ ...prev, search: undefined, page: 1 }))
    setIsAutocompleteOpen(false)
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleCreate = () => {
    setSelectedEmployee(null)
    setShowFormModal(true)
  }

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee)
    setShowFormModal(true)
  }

  const handleNotes = (employee: Employee) => {
    setSelectedEmployee(employee)
    setShowNotesModal(true)
  }

  const handleDeleteClick = (employee: Employee) => {
    setSelectedEmployee(employee)
    setShowDeleteConfirm(true)
  }

  const handleToggleClick = (employee: Employee) => {
    setSelectedEmployee(employee)
    if (employee.isActive) {
      // Confirm before deactivating
      setShowToggleConfirm(true)
    } else {
      // Activate directly
      handleToggleActive(employee)
    }
  }

  const handleToggleActive = async (employee?: Employee) => {
    const emp = employee || selectedEmployee
    if (!emp) return
    try {
      const updated = await employeeService.toggleActive(emp.id)
      toast.success(`Empleado ${updated.isActive ? 'activado' : 'desactivado'}`)
      setShowToggleConfirm(false)
      fetchEmployees()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true)
    try {
      if (selectedEmployee) {
        // Update
        const updateData: UpdateEmployeeData = { ...data }
        if (!data.password) delete updateData.password
        await employeeService.update(selectedEmployee.id, updateData)
        toast.success('Empleado actualizado')
      } else {
        // Create
        await employeeService.create(data as CreateEmployeeData)
        toast.success('Empleado creado')
      }
      setShowFormModal(false)
      fetchEmployees()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedEmployee) return
    try {
      await employeeService.delete(selectedEmployee.id)
      toast.success('Empleado eliminado')
      setShowDeleteConfirm(false)
      fetchEmployees()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleImpersonate = async (employee: Employee) => {
    try {
      toast.loading('Iniciando sesión como ' + employee.fullName + '...')
      const result = await authService.impersonate(employee.id)

      // Guardar el token y usuario actual del admin antes de cambiar
      const currentToken = localStorage.getItem('token')
      const currentUser = localStorage.getItem('user')
      if (currentToken && currentUser) {
        localStorage.setItem('adminToken', currentToken)
        localStorage.setItem('adminUser', currentUser)
      }

      // Guardar token y usuario del empleado
      localStorage.setItem('token', result.token)
      localStorage.setItem('user', JSON.stringify(result.user))

      // Marcar que estamos en modo impersonate
      localStorage.setItem('impersonating', employee.fullName)

      toast.dismiss()
      toast.success('Sesión iniciada como ' + employee.fullName)

      // Recargar la página para que el AuthContext detecte el nuevo usuario
      window.location.href = '/dashboard'
    } catch (error: unknown) {
      toast.dismiss()
      const { message } = extractApiErrors(error)
      toast.error(message)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl lg:text-3xl">
            <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-sm">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            Empleados
          </h1>
          <p className="page-subtitle text-sm sm:text-base">
            {pagination.total} empleados en total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle vista cards/tabla - solo visible en desktop */}
          <div className="hidden md:flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 transition-colors ${
                viewMode === 'cards'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 transition-colors border-l border-gray-200 dark:border-slate-700 ${
                viewMode === 'table'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
              title="Vista de tabla"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          {isAdmin && (
            <button onClick={handleCreate} className="btn btn-primary flex items-center gap-1.5 sm:gap-2 text-sm px-3 sm:px-4 py-2 ml-auto md:ml-0">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo Empleado</span>
              <span className="sm:hidden">Nuevo</span>
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div ref={autocompleteRef} className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              placeholder="Buscar por nombre, ID o email..."
              className="input pl-9 sm:pl-10 pr-9 sm:pr-10 w-full text-sm sm:text-base py-2 sm:py-2.5"
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setIsAutocompleteOpen(true)
              }}
              onFocus={() => searchTerm && setIsAutocompleteOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(searchTerm)
                  setIsAutocompleteOpen(false)
                }
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}

            {/* Dropdown autocomplete */}
            {isAutocompleteOpen && searchTerm && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                    Buscando...
                  </div>
                ) : autocompleteResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No se encontraron empleados
                  </div>
                ) : (
                  autocompleteResults.map((emp) => (
                    <div
                      key={emp.id}
                      onClick={() => handleSelectFromAutocomplete(emp)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                    >
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {emp.fullName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          <span className="font-mono text-xs bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded mr-2">
                            {emp.employeeId}
                          </span>
                          {emp.fullName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {emp.position} • {SHIFT_LABELS[emp.shift]} • {emp.workCenterName || WORK_CENTER_LABELS[emp.workCenterType]}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-3 gap-2 sm:gap-3">
            <select
              className="input text-sm py-2 sm:py-2.5"
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  workCenter: e.target.value as any || undefined,
                  page: 1,
                }))
              }
            >
              <option value="">Todos los centros</option>
              {Object.entries(WORK_CENTER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="input text-sm py-2 sm:py-2.5"
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  shift: e.target.value as any || undefined,
                  page: 1,
                }))
              }
            >
              <option value="">Todos los turnos</option>
              {Object.entries(SHIFT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="input text-sm py-2 sm:py-2.5"
              value={filters.isActive === undefined ? '' : filters.isActive ? 'active' : 'inactive'}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  isActive: e.target.value === '' ? undefined : e.target.value === 'active',
                  page: 1,
                }))
              }
            >
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
              <option value="">Todos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content - Cards or Table (en móvil siempre cards) */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
        </div>
      ) : (isMobile || viewMode === 'cards') ? (
        /* Vista de Cards */
        <div className="space-y-4">
          {employees.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <Users className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No hay empleados</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map((employee) => (
                <EmployeeCard
                  key={employee.id}
                  employee={employee}
                  isAdmin={isAdmin}
                  onView={(emp) => navigate(`/employees/${emp.id}`)}
                  onToggle={handleToggleClick}
                  onEdit={handleEdit}
                  onNotes={handleNotes}
                  onDelete={handleDeleteClick}
                  onImpersonate={handleImpersonate}
                />
              ))}
            </div>
          )}

          {/* Pagination para Cards */}
          {employees.length > 0 && (
            <div className="flex flex-col xs:flex-row items-center justify-between gap-3 p-3 sm:p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center xs:text-left">
                <span className="hidden xs:inline">Mostrando </span>
                {(pagination.page - 1) * pagination.limit + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)}
                <span className="hidden xs:inline"> de</span><span className="xs:hidden">/</span> {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2 sm:px-3 sm:py-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[60px] text-center">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2 sm:px-3 sm:py-2"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Vista de Tabla */
        <div className="table-container">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Empleado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Puesto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Centro
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Turno
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Días 5R
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                    Estado
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr
                    key={employee.id}
                    className={`table-row ${!employee.isActive ? 'opacity-50' : ''} ${isAdmin ? 'cursor-pointer' : ''}`}
                    onClick={() => isAdmin && navigate(`/employees/${employee.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                          {employee.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {employee.fullName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {employee.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded">
                        {employee.employeeId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {employee.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {employee.workCenterName || WORK_CENTER_LABELS[employee.workCenterType]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {SHIFT_LABELS[employee.shift]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {employee.daysAt5Shifts.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {employee.hasUser ? (
                          <span className="badge badge-success">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Con acceso
                          </span>
                        ) : (
                          <span className="badge bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                            <UserX className="h-3 w-3 mr-1" />
                            Sin acceso
                          </span>
                        )}
                        {!employee.isActive && (
                          <span className="badge badge-error">
                            Inactivo
                          </span>
                        )}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/employees/${employee.id}`)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleClick(employee)}
                            className={`p-2 rounded-lg transition-colors ${
                              employee.isActive
                                ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title={employee.isActive ? 'Desactivar' : 'Activar'}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleImpersonate(employee)}
                            className="p-2 hover:bg-success-50 dark:hover:bg-success-900/30 rounded-lg text-success-600 dark:text-success-400 transition-colors"
                            title="Entrar como este empleado"
                          >
                            <LogIn className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleNotes(employee)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                            title="Notas"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(employee)}
                            className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400 transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(employee)}
                            className="p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination para Tabla */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-slate-800/50">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{' '}
              de {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <EmployeeFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleFormSubmit}
        employee={selectedEmployee}
        isLoading={isSubmitting}
      />

      <EmployeeNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        employee={selectedEmployee}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar Empleado"
        message={`¿Estás seguro de que deseas eliminar a ${selectedEmployee?.fullName}? Se liberarán sus campos únicos (email, DNI, ID) para poder reutilizarlos. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showToggleConfirm}
        onClose={() => setShowToggleConfirm(false)}
        onConfirm={() => handleToggleActive()}
        title="Desactivar Empleado"
        message={`¿Estás seguro de que deseas desactivar a ${selectedEmployee?.fullName}? No podrá iniciar sesión hasta que sea reactivado.`}
        confirmText="Desactivar"
        variant="danger"
      />
    </div>
  )
}
